import React from 'react';

const STATUS_LABELS = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past Due',
  unpaid: 'Unpaid',
  canceled: 'Canceled',
};

export const buildMemberFilterGroups = ({
  venueOwnerships, statuses = [], daysFilter = null, extraFilters = [],
}) => {
  const groups = [];

  if (venueOwnerships && venueOwnerships.length > 0) {
    groups.push({
      label: 'Venues',
      filters: [
        {
          id: 'venue_ownership_id',
          label: 'Venue',
          type: 'checkbox',
          options: venueOwnerships.map((vo) => ({
            value: String(vo.id),
            label: vo.name || vo.venue_name || `Venue ${vo.id}`,
          })),
        },
      ],
    });
  }

  groups.push({
    label: 'Membership',
    filters: [
      ...(statuses.length > 0 ? [{
        id: 'status',
        label: 'Status',
        type: 'checkbox',
        options: statuses.map((value) => ({ value, label: STATUS_LABELS[value] })),
      }] : []),
      ...extraFilters,
      {
        id: 'interval',
        label: 'Billing',
        type: 'checkbox',
        options: [
          { value: 'month', label: 'Monthly' },
          { value: 'year', label: 'Annual' },
        ],
      },
      ...(daysFilter ? [
        { id: 'amount', label: 'Amount', type: 'range' },
        { id: daysFilter.id, label: daysFilter.label, type: 'range' },
      ] : []),
    ],
  });

  return groups;
};

export const buildMemberApiFilters = (daysFilterId, extraKeys = []) => (activeFilters) => {
  const params = {};
  ['venue_ownership_id', 'status', 'interval', ...extraKeys].forEach((key) => {
    if (activeFilters[key]?.length > 0) params[key] = activeFilters[key];
  });

  ['amount', daysFilterId].filter(Boolean).forEach((filterId) => {
    const range = activeFilters[filterId];
    if (!range) return;
    if (range.min != null && range.min !== '') params[`${filterId}_min`] = range.min;
    if (range.max != null && range.max !== '') params[`${filterId}_max`] = range.max;
  });

  return params;
};

const dateCell = (value, formatEventDate) => (
  <span className="text-nowrap">{value ? formatEventDate(value) : '—'}</span>
);

export const renderMemberCell = (dateColumnIds) => (col, row, { currencyFormatter: cf, formatEventDate, team }) => {
  if (dateColumnIds.includes(col.id)) return dateCell(row[col.id], formatEventDate);

  switch (col.id) {
    case 'member_name': {
      const name = row.member_name || '(No name)';
      if (team?.package?.crm && row.fan_id) {
        return (
          <a href={`/teams/${team.id}/crm/fans/${row.fan_id}`} style={{ color: 'var(--sl-fg-brand, #1982c4)' }}>
            {name}
          </a>
        );
      }
      return name;
    }
    case 'email': return row.email || '—';
    case 'product_name': return row.product_name || '—';
    case 'venue_name': return row.venue_name || '—';
    case 'status': return row.display_status || '—';
    case 'amount': return cf.format(row.amount || 0);
    case 'interval': return row.interval_label || '—';
    case 'member_since': return dateCell(row.member_since, formatEventDate);
    case 'months_as_member': return (row.months_as_member ?? 0).toLocaleString();
    case 'payment_type':
    case 'movement':
      return row[col.id] || '—';
    case 'amount_paid':
    case 'refunded':
    case 'net_to_venue':
    case 'processing_fee':
    case 'opendate_fee':
      return cf.format(row[col.id] || 0);
    default: return '—';
  }
};

export const memberColumns = (extraColumns, { hidden = [] } = {}) => {
  const allColumns = [
    { id: 'member_name', label: 'Member', defaultVisible: true, sortable: true, sortType: 'text' },
    { id: 'email', label: 'Email', defaultVisible: true, sortable: true, sortType: 'text' },
    { id: 'product_name', label: 'Membership', defaultVisible: true, sortable: true, sortType: 'text' },
    { id: 'status', label: 'Status', defaultVisible: true, sortable: true, sortType: 'text' },
    { id: 'amount', label: 'Amount', defaultVisible: true, sortable: true, sortType: 'currency' },
    { id: 'interval', label: 'Billing', defaultVisible: true, sortable: true, sortType: 'text' },
    ...extraColumns,
    { id: 'member_since', label: 'Member Since', defaultVisible: false, sortable: true, sortType: 'date' },
    { id: 'venue_name', label: 'Venue', defaultVisible: false, sortable: true, sortType: 'text' },
  ];
  const columns = allColumns.map((c) => (hidden.includes(c.id) ? { ...c, defaultVisible: false } : c));

  return {
    columns,
    getDefaultVisibleColumns: () => columns.filter((c) => c.defaultVisible),
    getDefaultHiddenColumns: () => columns.filter((c) => !c.defaultVisible && !c.alwaysVisible),
  };
};

export const memberReportBaseConfig = ({ slug, title, sidebarKey, localStorageName }) => ({
  pageTitle: title,
  sidebarKey,
  defaultPreset: 'year_to_date',
  showDatePicker: false,
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],
  localStorageKey: (teamId) => `${localStorageName}_${teamId}`,
  firstColumnId: 'member_name',
  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/memberships/${slug}/data.json`,
  detailResponseKey: 'members',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/memberships/${slug}/export`,
  detailErrorMessage: `Failed to load ${title.toLowerCase()} data.`,
  detailTableTitle: title,
  searchPlaceholder: 'Search members...',
  paginationLabel: 'members',
  itemLabel: (count) => `member${count !== 1 ? 's' : ''}`,
  filterTitle: 'Filter Members',
  filterSubtitle: 'Apply filters to narrow member data.',
  kpiGridColumns: 4,
});
